import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiGetSuivisForCoach, apiGetCycles, apiGetMentals, apiGetSports, apiGetCyclePhases, apiSearchSportive, apiCreateSuivi, apiGetSportive, apiGetDashboardSummary } from '../services/api';
import PageHeader from '../components/PageHeader';
import StaticConcentricPie from '../components/StaticConcentricPie';
import Spinner from '../components/Spinner';
import { phaseDisplay } from '../components/PhaseBadge';
import { MENTAL, SPORT, UI, COACH, CYCLE_PIE_COLORS, MENTAL_PIE_COLORS, SPORT_PIE_COLORS, toPhaseKey } from '../constants/colors';
import useTeams from '../hooks/useTeams';
import './Coach.css';
// Les pastilles d'équipe des cartes sportives réutilisent les classes de la page
// Équipes ; l'import garantit que ses styles sont chargés même si cette page-là
// n'a pas encore été visitée.
import './CoachTeams.css';
import { todayISO } from '../utils/formDate';
import { MOOD_EMOJI } from '../constants/labels';

const AVATAR_COLORS = [MENTAL.accent, MENTAL.estime, MENTAL.concentration, SPORT.rpeFacile, SPORT.primary];

// Tri « Phase » : ordre physiologique du cycle, puis phase inconnue, puis cycle non partagé.
const PHASE_RANK = { REGLES: 0, FOLLICULAIRE: 1, OVULATION: 2, LUTEALE: 3 };
const RANK_UNKNOWN = 4;
const RANK_UNSHARED = 5;

// Repère de performance sous le nom de la phase : ce que le coach peut en attendre à la séance.
const PHASE_HINT = {
  REGLES: 'Performance normale',
  FOLLICULAIRE: 'Prise de muscle optimale',
  OVULATION: 'Explosivité, énergie',
  LUTEALE: 'Endurance',
};

const SORTS = [
  { id: 'ajout', label: 'Ajout' },
  { id: 'phase', label: 'Phase' },
];

// Au-delà, les équipes d'une carte se résument en « +N » : la ligne reste sur une hauteur.
const MAX_TEAM_TAGS = 2;

const LockIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="11" width="16" height="10" rx="2"/>
    <path d="M8 11V7a4 4 0 0 1 8 0v4"/>
  </svg>
);

// Une case de la rangée d'indicateurs. Toujours rendue (valeur, « — » ou cadenas) :
// c'est ce qui donne la même géométrie à toutes les cartes.
const Metric = ({ label, value, locked, dot }) => (
  <div className="coach__metric" title={locked ? `${label} : non partagé` : undefined}>
    <span className="coach__metric-label">{label}</span>
    <span className="coach__metric-value">
      {locked ? (
        <><LockIcon /><span className="coach__sr-only">non partagé</span></>
      ) : value == null ? '—' : (
        <>
          {dot && <span className="coach__metric-dot" style={{ background: dot }} aria-hidden="true" />}
          {value}
        </>
      )}
    </span>
  </div>
);

const Coach = () => {
  const navigate = useNavigate();
  const { user, getToken } = useAuth();
  const [suivis, setSuivis] = useState([]);
  const [athletesData, setAthletesData] = useState({});
  const [profiles, setProfiles] = useState({});
  const [dashboardData, setDashboardData] = useState({});
  const [loading, setLoading] = useState(true);

  // ── Teams ──
  // Persistées côté serveur : le hook prend le jeton et remonte au passage les
  // équipes restées dans le localStorage d'avant.
  const { teams, error: teamsError, getTeamsForMember } = useTeams(user?.id, getToken());
  const [activeTeamFilter, setActiveTeamFilter] = useState(null);
  const [sortBy, setSortBy] = useState('ajout');

  // ── Add sportive modal ──
  const [showModal, setShowModal] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');

  const openModal = () => {
    setShowModal(true);
    setSearchEmail('');
    setSearchResult(null);
    setSearchError('');
    setAddError('');
  };

  const closeModal = () => setShowModal(false);

  const handleSearch = async () => {
    if (!searchEmail.trim()) return;
    setSearchLoading(true);
    setSearchError('');
    setSearchResult(null);
    try {
      const result = await apiSearchSportive(searchEmail.trim(), getToken());
      setSearchResult(result);
    } catch (err) {
      setSearchError(err.message || 'Aucune sportive trouvée.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddSportive = async () => {
    if (!searchResult) return;
    setAddLoading(true);
    setAddError('');
    try {
      // Ni date de début ni de fin : le suivi commence à l'invitation, et c'est la
      // sportive qui choisit la période de données qu'elle partage.
      await apiCreateSuivi({ id_sportive: searchResult.id }, getToken());
      // Refresh the list
      const suiviList = await apiGetSuivisForCoach(user.id, getToken());
      setSuivis(suiviList);
      const dataMap = { ...athletesData };
      const sid = searchResult.id;
      if (!dataMap[sid]) {
        const [cycles, mentals, sports, phases, dash] = await Promise.all([
          apiGetCycles(sid, getToken()).catch(() => []),
          apiGetMentals(sid, getToken()).catch(() => []),
          apiGetSports(sid, getToken()).catch(() => []),
          apiGetCyclePhases(sid, getToken()).catch(() => []),
          apiGetDashboardSummary(sid, getToken()).catch(() => null),
        ]);
        dataMap[sid] = { cycles, mentals, sports, phases };
        setAthletesData(dataMap);
        setProfiles(prev => ({ ...prev, [sid]: searchResult }));
        if (dash) setDashboardData(prev => ({ ...prev, [sid]: dash }));
      }
      closeModal();
    } catch (err) {
      setAddError(err.message || 'Erreur lors de l\'ajout.');
    } finally {
      setAddLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.id) { setLoading(false); return; }
    const token = getToken();
    apiGetSuivisForCoach(user.id, token)
      .then(async (suiviList) => {
        setSuivis(suiviList);
        const dataMap = {};
        const profileMap = {};
        const dashMap = {};
        await Promise.all(suiviList.map(async (s) => {
          const sid = s.id_sportive;
          try {
            const [cycles, mentals, sports, phases, profile, dash] = await Promise.all([
              apiGetCycles(sid, token).catch(() => []),
              apiGetMentals(sid, token).catch(() => []),
              apiGetSports(sid, token).catch(() => []),
              apiGetCyclePhases(sid, token).catch(() => []),
              apiGetSportive(sid, token).catch(() => null),
              apiGetDashboardSummary(sid, token).catch(() => null),
            ]);
            dataMap[sid] = { cycles, mentals, sports, phases };
            if (profile) profileMap[sid] = profile;
            if (dash) dashMap[sid] = dash;
          } catch {
            dataMap[sid] = { cycles: [], mentals: [], sports: [], phases: [] };
          }
        }));
        setAthletesData(dataMap);
        setProfiles(profileMap);
        setDashboardData(dashMap);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user?.id, getToken]);

  const today = todayISO();

  // Compute summary stats
  const memberCount = suivis.length;
  const allSports = Object.values(athletesData).flatMap(d => d.sports || []);
  const rpeVals = allSports.filter(s => s.rpe != null).map(s => s.rpe);
  const avgRpe = rpeVals.length ? (rpeVals.reduce((a, b) => a + b, 0) / rpeVals.length).toFixed(1) : null;
  const upToDate = suivis.filter(s => {
    const d = athletesData[s.id_sportive];
    return d && (d.cycles.some(c => c.date === today) || d.mentals.some(m => m.date === today) || d.sports.some(sp => sp.date === today));
  }).length;

  // Mêmes couleurs RPE que côté sportive (Home/Sport) : vert clair ≤ 5, bleu > 5.
  const rpeBg = avgRpe == null
    ? SPORT.jourRepos
    : Number(avgRpe) > 5 ? SPORT.rpeIntense : SPORT.rpeFacile;

  const STATS_DYNAMIC = [
    {
      id: 'members',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      ),
      value: String(memberCount),
      label: 'Membres',
      bg: 'var(--sport-primary)',
    },
    {
      id: 'rpe',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
          <polyline points="17 6 23 6 23 12"/>
        </svg>
      ),
      value: avgRpe ?? '—',
      label: 'RPE moyen',
      bg: rpeBg,
    },
    {
      id: 'uptodate',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="9 12 11 14 15 10"/>
        </svg>
      ),
      value: memberCount ? `${upToDate}/${memberCount}` : '—',
      label: 'À jour',
      bg: MENTAL.accent,
    },
    {
      id: 'mood',
      icon: <span className="coach__stat-emoji">😊</span>,
      value: null,
      label: 'Humeur générale',
      bg: MENTAL.concentration,
    },
  ];

  const filteredSuivis = activeTeamFilter === null
    ? suivis
    : suivis.filter(s => getTeamsForMember(s.id_sportive).some(t => t.id === activeTeamFilter));

  // Phase *actuelle* (item de /phases) par sportive, calculée une fois pour le bandeau
  // et le tri. Null si la fenêtre de partage s'arrête avant aujourd'hui : le dernier
  // jour visible n'est alors pas la phase en cours.
  const phaseToday = {};
  filteredSuivis.forEach(s => {
    const phases = athletesData[s.id_sportive]?.phases || [];
    const last = phases.reduce((acc, p) => (!acc || p.date > acc.date ? p : acc), null);
    phaseToday[s.id_sportive] = last?.date === today ? last : null;
  });
  const phaseRank = (s) => {
    if (!s.partage_cycle) return RANK_UNSHARED;
    return PHASE_RANK[toPhaseKey(phaseToday[s.id_sportive]?.phase)] ?? RANK_UNKNOWN;
  };
  // Tri stable : à phase et jour égaux, l'ordre d'ajout est conservé.
  const displayedSuivis = sortBy === 'phase'
    ? [...filteredSuivis].sort((a, b) =>
        phaseRank(a) - phaseRank(b)
        || (phaseToday[a.id_sportive]?.cycle_day ?? 0) - (phaseToday[b.id_sportive]?.cycle_day ?? 0))
    : filteredSuivis;

  // Ligne d'équipes réservée sur toutes les cartes dès qu'une seule en a,
  // pour que les cartes restent alignées.
  const showTeamsRow = filteredSuivis.some(s => getTeamsForMember(s.id_sportive).length > 0);

  return (
    <div className="coach">
      <PageHeader title="Dashboard coach" />

      {/* ── Add sportive button ── */}
      <button className="coach__add-btn" onClick={openModal}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        Ajouter une sportive
      </button>

      {/* ── Team filter bar ── */}
      <div className="coach__team-bar">
        <button
          className={`coach__team-chip${activeTeamFilter === null ? ' coach__team-chip--active' : ''}`}
          style={activeTeamFilter === null ? { background: 'var(--color-primary)', color: 'var(--c-white)' } : {}}
          onClick={() => setActiveTeamFilter(null)}
        >
          Toutes
        </button>
        {teams.map(team => (
          <button
            key={team.id}
            className={`coach__team-chip${activeTeamFilter === team.id ? ' coach__team-chip--active' : ''}`}
            style={activeTeamFilter === team.id
              ? { background: team.color, color: 'var(--c-white)' }
              : { borderColor: team.color, color: team.color }}
            onClick={() => setActiveTeamFilter(prev => prev === team.id ? null : team.id)}
          >
            {team.name}
          </button>
        ))}
        <button className="coach__team-manage-btn" onClick={() => navigate('/coach/equipes')}>
          Gérer les équipes
        </button>
      </div>

      {/* Les équipes sont désormais en base : un échec réseau doit se voir, sinon
          une création qui n'a pas abouti passerait pour un simple rien. */}
      {teamsError && <p className="coach__team-error">Équipes : {teamsError}</p>}

      {/* ── Stat cards ── */}
      <div className="coach__stats-grid">
        {STATS_DYNAMIC.map(s => (
          <div key={s.id} className="coach__stat-card" style={{ background: s.bg }}>
            <div className="coach__stat-icon">{s.icon}</div>
            {s.value && <div className="coach__stat-value">{s.value}</div>}
            <div className="coach__stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Athlete list ── */}
      {loading ? (
        <div className="coach__loading">
          <Spinner label="Chargement des sportives" />
        </div>
      ) : suivis.length === 0 ? (
        <p style={{ textAlign: 'center', color: UI.textEmpty, padding: '2rem 0' }}>Aucune sportive liée</p>
      ) : (
        <>
        {filteredSuivis.length > 1 && (
          <div className="coach__sort" role="group" aria-label="Trier les sportives">
            <span className="coach__sort-label">Trier par</span>
            <div className="coach__sort-options">
              {SORTS.map(o => (
                <button
                  key={o.id}
                  type="button"
                  className={`coach__sort-btn${sortBy === o.id ? ' coach__sort-btn--active' : ''}`}
                  aria-pressed={sortBy === o.id}
                  onClick={() => setSortBy(o.id)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="coach__athletes">
          {filteredSuivis.length === 0 && (
            <p style={{ textAlign: 'center', color: UI.textEmpty, padding: '1rem 0' }}>
              Aucune sportive dans cette équipe.
            </p>
          )}
          {displayedSuivis.map((s) => {
            const sid = s.id_sportive;
            // Index dans la liste complète : la couleur d'avatar ne change ni au tri ni au filtre.
            const idx = suivis.indexOf(s);
            const d = athletesData[sid] || { cycles: [], mentals: [], sports: [], phases: [] };
            const p = profiles[sid];
            const lastSport = [...d.sports].sort((a, b) => b.date.localeCompare(a.date))[0];
            const lastMental = [...d.mentals].sort((a, b) => b.date.localeCompare(a.date))[0];
            const moodEmoji = lastMental?.mood ? (MOOD_EMOJI[lastMental.mood] || '😶') : null;
            const fullName = p ? `${p.prenom} ${p.nom}` : `Sportive #${sid}`;
            const initials = p ? `${p.prenom[0]}${p.nom[0]}` : `S${idx + 1}`;
            const avatarBg = AVATAR_COLORS[idx % AVATAR_COLORS.length];
            // Couleur RPE identique à celle vue par la sportive (vert clair ≤ 5, bleu > 5).
            const rpeColor = lastSport?.rpe != null ? (lastSport.rpe <= 5 ? SPORT.rpeFacile : SPORT.rpeIntense) : null;
            // Volets partagés par la sportive : on ne montre les indicateurs que si autorisés.
            const canCycle = s.partage_cycle;
            const canMental = s.partage_mental;
            const canSport = s.partage_sport;
            const phase = canCycle ? phaseDisplay(phaseToday[sid]?.phase) : null;
            const phaseHint = phase ? PHASE_HINT[toPhaseKey(phaseToday[sid].phase)] : null;
            const memberTeams = getTeamsForMember(sid);
            const dash = dashboardData[sid];
            return (
              <div
                key={sid}
                className="coach__athlete-card"
                onClick={() => navigate(`/coach/sportive/${sid}`, { state: { profile: p, suivi: s } })}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/coach/sportive/${sid}`, { state: { profile: p, suivi: s } }); } }}
              >
                {/* Bandeau toujours présent, même sans phase : il fixe le haut de la carte. */}
                <div
                  className={`coach__athlete-phase${phase ? '' : ' coach__athlete-phase--none'}`}
                  style={phase ? { '--phase-color': phase.color, color: phase.textColor } : undefined}
                  title={phase ? `Phase actuelle : ${phase.label}` : undefined}
                >
                  {phase ? (
                    <>
                      <span className="coach__athlete-phase-label">
                        <span className="coach__athlete-phase-dot" aria-hidden="true" />
                        {phase.label}
                      </span>
                      <span className="coach__athlete-phase-hint">{phaseHint}</span>
                    </>
                  ) : canCycle ? 'Phase inconnue' : (
                    <><LockIcon /> Cycle non partagé</>
                  )}
                </div>
                <div className="coach__athlete-header">
                  <div className="coach__athlete-avatar" style={{ background: avatarBg }}>
                    {initials}
                  </div>
                  <div className="coach__athlete-info">
                    <span className="coach__athlete-name" title={fullName}>{fullName}</span>
                    <span className="coach__athlete-age">{p?.sport || 'Sportive'}</span>
                  </div>
                  <span className="coach__athlete-chevron" aria-hidden="true">›</span>
                </div>
                <div className="coach__athlete-pie">
                  <div className="coach__athlete-pie-chart">
                    <StaticConcentricPie
                      cycleSlices={dash?.cycle_pie?.map(s => ({ ...s, color: CYCLE_PIE_COLORS[s.label] ?? s.color }))}
                      mentalSlices={dash?.mental_pie?.map(s => ({ ...s, color: MENTAL_PIE_COLORS[s.label] ?? s.color }))}
                      sportSlices={dash?.sport_pie?.map(s => ({ ...s, color: SPORT_PIE_COLORS[s.label] ?? s.color }))}
                      cycleDayPct={dash?.cycle_day_pct ?? null}
                      cycleDay={dash?.cycle_day ?? null}
                      cycleDays={dash?.cycle_length || 28}
                    />
                  </div>
                </div>
                <div className="coach__athlete-metrics">
                  <Metric label="RPE" locked={!canSport} value={lastSport?.rpe} dot={rpeColor} />
                  <Metric label="FC" locked={!canSport} value={lastSport?.effort_heart_rate} />
                  <Metric label="Humeur" locked={!canMental} value={moodEmoji} />
                </div>
                {showTeamsRow && (
                  <div className="coach__athlete-teams">
                    {memberTeams.slice(0, MAX_TEAM_TAGS).map(t => (
                      <span
                        key={t.id}
                        className="coach__team-tag"
                        style={{ background: t.color + '22', color: t.color, borderColor: t.color + '55' }}
                        title={t.name}
                      >
                        {t.name}
                      </span>
                    ))}
                    {memberTeams.length > MAX_TEAM_TAGS && (
                      <span
                        className="coach__team-tag coach__team-tag--more"
                        title={memberTeams.slice(MAX_TEAM_TAGS).map(t => t.name).join(', ')}
                      >
                        +{memberTeams.length - MAX_TEAM_TAGS}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </>
      )}
      {/* ── Add sportive modal ── */}
      {showModal && (
        <div className="coach__modal-overlay" onClick={closeModal}>
          <div className="coach__modal" onClick={e => e.stopPropagation()}>
            <div className="coach__modal-header">
              <h3 className="coach__modal-title">Ajouter une sportive</h3>
              <button className="coach__modal-close" onClick={closeModal} aria-label="Fermer">✕</button>
            </div>

            {!searchResult ? (
              <>
                <p className="coach__modal-sub">Entrez l'email de la sportive pour la trouver.</p>
                <div className="coach__modal-row">
                  <input
                    className="coach__modal-input"
                    type="email"
                    placeholder="email@exemple.com"
                    value={searchEmail}
                    onChange={e => setSearchEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  />
                  <button className="coach__modal-search-btn" onClick={handleSearch} disabled={searchLoading}>
                    {searchLoading ? '…' : 'Chercher'}
                  </button>
                </div>
                {searchError && <p className="coach__modal-error">{searchError}</p>}
              </>
            ) : (
              <>
                <div className="coach__modal-found">
                  <div className="coach__modal-avatar">{searchResult.prenom?.[0]}{searchResult.nom?.[0]}</div>
                  <div>
                    <p className="coach__modal-found-name">{searchResult.prenom} {searchResult.nom}</p>
                    <p className="coach__modal-found-email">{searchResult.mail}</p>
                  </div>
                </div>
                <p className="coach__modal-hint">
                  Elle recevra une invitation et choisira elle-même les données et la période qu'elle te partage.
                </p>
                {addError && <p className="coach__modal-error">{addError}</p>}
                <div className="coach__modal-actions">
                  <button className="coach__modal-cancel" onClick={() => setSearchResult(null)}>← Retour</button>
                  <button className="coach__modal-confirm" onClick={handleAddSportive} disabled={addLoading}>
                    {addLoading ? 'Ajout…' : 'Confirmer'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Coach;
